let mapInitialized = false;
const OPENFREEMAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';
const OSM_RASTER_FALLBACK_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

let leafletLoaded = false;
let leafletLoadPromise = null;

function loadLeafletModule() {
  if (leafletLoaded && typeof L !== 'undefined') {
    return Promise.resolve(L);
  }

  if (!leafletLoadPromise) {
    leafletLoadPromise = Promise.all([
      import('leaflet'),
      import('leaflet/dist/leaflet.css'),
      import('maplibre-gl'),
      import('maplibre-gl/dist/maplibre-gl.css'),
      import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url')
    ])
      .then(async ([leaflet, , maplibre, , workerUrlModule]) => {
        const leafletLib = leaflet?.default || leaflet;
        const maplibreLib = maplibre?.default || maplibre;

        // MapLibre resolves its worker relative to its own chunk URL, which
        // Vite doesn't emit. Hand it the Vite-bundled worker URL instead
        // (`?worker&url` emits a .js asset — a raw .mjs copy was served as
        // text/plain in production, which browsers refuse to run as a
        // module worker; setWorkerUrl is a named export, not on the default).
        const setWorkerUrl = maplibre?.setWorkerUrl || maplibreLib.setWorkerUrl;
        if (typeof setWorkerUrl === 'function' && workerUrlModule?.default) {
          setWorkerUrl(workerUrlModule.default);
        }

        // Ensure global compatibility for existing Leaflet usage. The
        // maplibre-gl-leaflet plugin also expects both libraries on window.
        if (!window.L) {
          window.L = leafletLib;
        }
        if (!window.maplibregl) {
          window.maplibregl = maplibreLib;
        }

        // Registers L.maplibreGL — the base map is OpenFreeMap vector tiles.
        await import('@maplibre/maplibre-gl-leaflet');

        leafletLoaded = true;
        return leafletLib;
      })
      .catch((error) => {
        leafletLoadPromise = null;
        console.error('Failed to load Leaflet module', error);
        throw error;
      });
  }

  return leafletLoadPromise;
}

const HTML_ESCAPE_LOOKUP = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};

function escapeHtml(value) {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value).replace(/[&<>"']/g, (char) => HTML_ESCAPE_LOOKUP[char] || char);
}

function formatYelpPrice(value) {
  if (value === null || value === undefined) {
    return '';
  }

  let numericValue = value;
  if (typeof numericValue === 'string') {
    numericValue = numericValue.replace(/[^\d.]/g, '');
  }

  const parsed = Number(numericValue);
  if (Number.isFinite(parsed) && parsed > 0) {
    return `~$${Math.round(parsed).toLocaleString()}pp`;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }

  return '';
}

const mapState = {
  map: null,
  markerLayer: null,
  markers: [],
  activePopup: null,
  mapClickHandler: null,
  mapElement: null,
  dataRequest: null,
  observer: null,
  placeholder: null,
  pendingEventsData: null
};

const localEventsState = {
  container: null,
  panel: null,
  listItems: null,
  button: null,
  closeButton: null,
  countLabel: null,
  titleCountLabel: null,
  initialized: false,
  handlers: {
    toggle: null,
    close: null,
    keydown: null
  }
};

function getMapElementId() {
  return window.singleEvent ? 'eventMap' : 'map';
}

function getMapElement() {
  const elementId = getMapElementId();
  if (!mapState.mapElement || mapState.mapElement.id !== elementId) {
    mapState.mapElement = document.getElementById(elementId) || null;
  }
  return mapState.mapElement;
}

function resetMapElementCache() {
  mapState.mapElement = null;
}

function ensureMapPanes(map) {
  if (!map.getPane('yelpMarkersPane')) {
    const yelpPane = map.createPane('yelpMarkersPane');
    yelpPane.style.zIndex = 14;
  }

  if (!map.getPane('eventMarkersPane')) {
    const eventPane = map.createPane('eventMarkersPane');
    eventPane.style.zIndex = 16;
  }
}

function computeInitialView() {
  const urlParams = new URLSearchParams(window.location.search);
  const lat = parseFloat(urlParams.get('latitude'));
  const lng = parseFloat(urlParams.get('longitude'));

  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return {
      center: [lat, lng],
      zoom: 12
    };
  }

  return {
    center: [0, 0],
    zoom: 2
  };
}

function ensureMarkerLayer(map) {
  if (!mapState.markerLayer) {
    mapState.markerLayer = L.layerGroup().addTo(map);
  }
  return mapState.markerLayer;
}

function attachMapClickHandler() {
  const map = mapState.map;
  if (!map || mapState.mapClickHandler) {
    return;
  }

  mapState.mapClickHandler = () => {
    if (mapState.activePopup) {
      mapState.activePopup.closePopup();
      mapState.activePopup = null;
    }
  };

  map.on('click', mapState.mapClickHandler);
}

function clearMapClickHandler() {
  if (mapState.map && mapState.mapClickHandler) {
    mapState.map.off('click', mapState.mapClickHandler);
    mapState.mapClickHandler = null;
  }
}

function clearMarkers() {
  if (mapState.markerLayer) {
    mapState.markerLayer.clearLayers();
  }

  mapState.markers = [];
  mapState.activePopup = null;
}

function supportsWebGL2() {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGL2RenderingContext && canvas.getContext('webgl2'));
  } catch (error) {
    return false;
  }
}

// Base map: OpenFreeMap vector tiles (https://openfreemap.org) — free, no
// API key, no request limits. Rendered by MapLibre GL under Leaflet so the
// marker/popup code is unchanged. MapLibre needs WebGL2; the few browsers
// without it get plain OpenStreetMap raster tiles instead of no map at all.
function addBaseLayer(map) {
  if (supportsWebGL2() && typeof L.maplibreGL === 'function') {
    let vectorLayer = null;
    try {
      vectorLayer = L.maplibreGL({
        style: OPENFREEMAP_STYLE_URL,
        attribution: '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> © <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a> Data from <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>'
      });
      vectorLayer.addTo(map);
      return;
    } catch (error) {
      console.warn('MapLibre base layer failed, falling back to raster tiles', error);
      if (vectorLayer && map.hasLayer(vectorLayer)) {
        map.removeLayer(vectorLayer);
      }
    }
  }

  L.tileLayer(OSM_RASTER_FALLBACK_URL, {
    attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors',
    maxZoom: 19
  }).addTo(map);
}

function ensureMap() {
  if (typeof L === 'undefined') {
    return null;
  }

  if (mapState.map) {
    return mapState.map;
  }

  const mapElement = getMapElement();
  if (!mapElement) {
    console.error('Map container not found:', getMapElementId());
    return null;
  }

  const { center, zoom } = computeInitialView();

  // Clear the static address fallback — unlike Google Maps, Leaflet keeps
  // existing children in the container.
  const mapFallback = mapElement.querySelector('[data-map-fallback]');
  if (mapFallback) {
    mapFallback.remove();
  }

  const map = L.map(mapElement, {
    center,
    zoom,
    zoomControl: true,
    attributionControl: true
  });

  addBaseLayer(map);
  map.setMaxZoom(19);

  mapElement.style.borderRadius = '17px';
  map.attributionControl.setPrefix('');

  ensureMapPanes(map);
  ensureMarkerLayer(map);

  mapState.map = map;
  attachMapClickHandler();

  return map;
}

function cleanupMap() {
  const mapElement = mapState.mapElement || getMapElement();

  if (mapState.markerLayer) {
    mapState.markerLayer.clearLayers();
    mapState.markerLayer.remove();
    mapState.markerLayer = null;
  }

  clearMapClickHandler();

  if (mapState.map) {
    mapState.map.remove();
    mapState.map = null;
  }

  if (mapElement) {
    mapElement.innerHTML = '';
    delete mapElement._leaflet_id;
    mapElement.style.display = '';
  }

  mapState.markers = [];
  mapState.activePopup = null;
  mapState.dataRequest = null;
  mapState.pendingEventsData = null;

  if (mapState.observer && mapState.placeholder) {
    mapState.observer.unobserve(mapState.placeholder);
  }

  mapState.observer = null;
  mapState.placeholder = null;

  resetLocalEventsState();
  mapInitialized = false;
  resetMapElementCache();
}

function bindPopupTracking(marker) {
  marker.on('popupopen', () => {
    mapState.activePopup = marker;
  });

  marker.on('popupclose', () => {
    if (mapState.activePopup === marker) {
      mapState.activePopup = null;
    }
  });
}

function handleMarkerClick(marker) {
  if (mapState.activePopup && mapState.activePopup !== marker) {
    mapState.activePopup.closePopup();
  }
  mapState.activePopup = marker;
}

function createCustomMarker(latlng, event) {
  const map = ensureMap();
  if (!map) {
    return null;
  }

  const markerLayer = ensureMarkerLayer(map);
  const markerStyles = window.mapMarkerStyles || {};
  const eventStyle = markerStyles.event || {};
  const yelpStyle = markerStyles.yelp || {};
  const isYelp = event.is_yelp === true || event.is_yelp === 'true';

  const eventUrl = event.event_url || '#';
  const eventTitle = event.title || event.name || 'Untitled Event';
  const safeEventTitle = escapeHtml(eventTitle);
  const safeEventUrl = escapeHtml(eventUrl);
  const summaryText = event.platform_summary || 'No summary available';
  const rawPrice = Number.parseFloat(event.price);
  const hasPrice = Number.isFinite(rawPrice) && rawPrice > 0;
  const roundedPrice = hasPrice ? Math.round(rawPrice) : null;
  const formattedPrice = roundedPrice !== null
    ? `$${roundedPrice.toLocaleString()}`
    : null;
  const priceLabel = formattedPrice
    ? `${isYelp ? 'Est. ' : ''}${formattedPrice} pp`
    : null;
  const rawRating = Number.parseFloat(event.average_rating);
  const hasRating = Number.isFinite(rawRating);
  const averageRating = hasRating ? rawRating.toFixed(2) : null;
  const reviewsCountRaw = event.all_reviews_count ?? event.reviews_count ?? 0;
  const reviewsCount = Number(reviewsCountRaw) || 0;
  const reviewsCountLabel = reviewsCount > 0 ? reviewsCount.toLocaleString() : '0';
  let imageUrl = null;
  if (!isYelp) {
    imageUrl = event.image_url || (event.image && event.image.url) || window.eventImageMissingPath;
  }
  const hasImage = Boolean(imageUrl);
  const safeImageUrl = hasImage ? escapeHtml(imageUrl) : '';

  const ratingMarkup = !isYelp && hasRating
    ? `
        <span class="ds-map-popup__rating-pill inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-sm font-bold text-amber-700"
              aria-label="${escapeHtml(`${averageRating} out of 5 stars based on ${reviewsCountLabel} reviews`)}">
          <i data-lucide="star" class="lucide-icon rating-star text-amber-500" aria-hidden="true"></i>
          <span>${escapeHtml(averageRating)}</span>
          <span class="text-sm font-normal text-neutral-500">(${escapeHtml(reviewsCountLabel)})</span>
        </span>
      `
    : '';
  const hasSummary = summaryText && summaryText !== 'No summary available';
  const priceMarkup = priceLabel
    ? `<span class="ds-map-popup__price">${escapeHtml(priceLabel)}</span>`
    : '';

  let popupContent;

  if (isYelp) {
    const websiteUrl = event.website_url ? escapeHtml(event.website_url) : '';
    const yelpPriceValue = formatYelpPrice(event.ai_price || event.price);
    const summaryMarkup = summaryText && summaryText !== 'No summary available'
      ? `<p class="ds-map-popup__summary">${escapeHtml(summaryText)}</p>`
      : '';
    const pricePillMarkup = yelpPriceValue
      ? `<span class="ds-map-popup__price">${escapeHtml(yelpPriceValue)}</span>`
      : '';
    const actionMarkup = websiteUrl
      ? `
            <a href="${websiteUrl}"
               class="ds-map-popup__cta ds-map-popup__cta--yelp"
               target="_blank"
               rel="noopener"
               aria-label="Open ${escapeHtml(eventTitle)} website in a new tab">
              <span>Learn more</span>
              <svg aria-hidden="true"
                   class="ds-map-popup__cta-icon"
                   viewBox="0 0 20 20"
                   fill="currentColor">
                <path d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"/>
              </svg>
            </a>
          `.trim()
      : '';
    const actionsMarkup = actionMarkup || pricePillMarkup
      ? `<div class="ds-map-popup__actions">${actionMarkup || ''}${pricePillMarkup || ''}</div>`
      : '';

    const closeMarkup = `
        <button type="button"
                class="ds-map-popup__dismiss"
                aria-label="Close"
                data-map-popup-dismiss>
          <i data-lucide="x" class="lucide-icon" aria-hidden="true"></i>
        </button>
      `;

    popupContent = `
        <div class="ds-map-popup ds-map-popup--yelp">
          ${closeMarkup}
          <div class="ds-map-popup__body">
           <div class="ds-map-popup__title-row">
             <h3 class="ds-map-popup__title">${escapeHtml(eventTitle)}</h3>
           </div>
            ${summaryMarkup}
            ${actionsMarkup}
          </div>
        </div>
      `;

    const popupOptions = {
      autoPan: true,
      closeButton: false,
      className: 'ds-map-popup-shell'
    };

    const yelpRadius = Number(yelpStyle.radius) || 5;
    const yelpColor = yelpStyle.color || '#3B82F6';
    const yelpFill = yelpStyle.fillColor || yelpColor;
    const yelpFillOpacity = yelpStyle.fillOpacity ?? 0.85;
    const yelpWeight = yelpStyle.weight ?? 2;

    const circleMarker = L.circleMarker(latlng, {
      radius: yelpRadius,
      color: yelpColor,
      weight: yelpWeight,
      fillColor: yelpFill,
      fillOpacity: yelpFillOpacity,
      pane: map.getPane('yelpMarkersPane') ? 'yelpMarkersPane' : undefined
    });

    circleMarker.bindPopup(popupContent, {
      ...popupOptions,
      offset: [0, -(yelpRadius + 16)]
    });

    circleMarker.on('click', () => handleMarkerClick(circleMarker));
    bindPopupTracking(circleMarker);

    circleMarker.on('popupopen', (evt) => {
      const popupElement = evt?.popup?.getElement?.();
      if (!popupElement) return;

      const dismissButton = popupElement.querySelector('[data-map-popup-dismiss]');
      if (dismissButton) {
        dismissButton.addEventListener('click', () => {
          evt.popup.close();
        }, { once: true });
      }
    });

    circleMarker.addTo(markerLayer);
    return circleMarker;
  }

  const summaryMarkup = hasSummary
    ? `<p class="ds-map-popup__summary">${escapeHtml(summaryText)}</p>`
    : '';

  const mediaMarkup = hasImage
    ? `
            <div class="ds-map-popup__media">
              <a href="${safeEventUrl}" aria-label="View ${safeEventTitle}">
                <img src="${safeImageUrl}"
                     alt="${safeEventTitle}"
                     title="${safeEventTitle}"
                     loading="lazy"
                     onerror="this.src='${window.eventImageMissingPath}'" />
              </a>
              ${ratingMarkup}
            </div>
      `
    : '';
  const ratingInlineMarkup = hasImage ? '' : ratingMarkup;

  popupContent = `
        <div class="ds-map-popup ds-map-popup--event">
          <button type="button"
                  class="ds-map-popup__dismiss"
                  aria-label="Close"
                  data-map-popup-dismiss>
            <i data-lucide="x" class="lucide-icon" aria-hidden="true"></i>
          </button>
          ${mediaMarkup}
          <div class="ds-map-popup__body">
            <div class="ds-map-popup__title-row">
              <a href="${safeEventUrl}" class="ds-map-popup__title-link">
                <h3 class="ds-map-popup__title">${safeEventTitle}</h3>
              </a>
            </div>
            ${ratingInlineMarkup}
            ${summaryMarkup}
            <div class="ds-map-popup__actions">
              <a href="${safeEventUrl}"
                 class="ds-map-popup__cta"
                 aria-label="View experience for ${safeEventTitle}"
                 data-map-popup-cta>
                <span>View experience</span>
                <svg aria-hidden="true"
                     class="ds-map-popup__cta-icon"
                     viewBox="0 0 20 20"
                     fill="currentColor">
                  <path d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"/>
                </svg>
              </a>
              ${priceMarkup}
            </div>
          </div>
        </div>
      `;

  const popupOptions = {
    autoPan: true,
    closeButton: false,
    className: 'ds-map-popup-shell'
  };

  const iconSize = Array.isArray(eventStyle.iconSize) && eventStyle.iconSize.length === 2
    ? eventStyle.iconSize
    : [28, 28];
  const iconAnchor = Array.isArray(eventStyle.iconAnchor) && eventStyle.iconAnchor.length === 2
    ? eventStyle.iconAnchor
    : [Math.round(iconSize[0] / 2), Math.round(iconSize[1] / 2)];
  const popupOffset = Array.isArray(eventStyle.popupAnchor) && eventStyle.popupAnchor.length === 2
    ? eventStyle.popupAnchor
    : [0, -Math.max(iconSize[1], 32)];
  const markerClassName = eventStyle.markerClass || 'ds-map-legend__icon--bookable';
  const markerHtml = `
      <span class="map-marker map-marker--event" aria-hidden="true">
        <span class="map-marker__icon ds-map-legend__icon ${markerClassName}"></span>
      </span>
    `;

  const customIcon = L.divIcon({
    html: markerHtml,
    className: 'map-marker-container map-marker-container--event',
    iconSize,
    iconAnchor,
    popupAnchor: popupOffset
  });

  const marker = L.marker(latlng, {
    icon: customIcon,
    riseOnHover: true,
    pane: map.getPane('eventMarkersPane') ? 'eventMarkersPane' : undefined,
    zIndexOffset: -50
  });

  marker.bindPopup(popupContent, {
    ...popupOptions,
    offset: popupOffset
  });

  marker.on('click', () => handleMarkerClick(marker));
  bindPopupTracking(marker);
  marker.on('popupopen', (evt) => {
    const popupElement = evt?.popup?.getElement?.();
    if (!popupElement) return;

    const dismissButton = popupElement.querySelector('[data-map-popup-dismiss]');
    if (dismissButton) {
      dismissButton.addEventListener('click', () => {
        evt.popup.close();
      }, { once: true });
    }
  });

  marker.addTo(markerLayer);
  return marker;
}

function updateMap(eventsData) {
  let events;
  if (Array.isArray(eventsData)) {
    events = eventsData;
  } else if (eventsData && typeof eventsData === 'object') {
    events = [eventsData];
  } else {
    events = [];
  }

  mapState.pendingEventsData = events;

  const map = ensureMap();
  if (!map) {
    return;
  }

  mapState.pendingEventsData = null;

  clearMarkers();

  if (events.length === 0) {
    const { center, zoom } = computeInitialView();
    map.setView(center, zoom);
    return;
  }

  const bounds = L.latLngBounds();
  const nextMarkers = [];

  events.forEach((event) => {
    const lat = parseFloat(event.latitude);
    const lng = parseFloat(event.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    const marker = createCustomMarker([lat, lng], event);
    if (marker && marker.getLatLng) {
      nextMarkers.push(marker);
      bounds.extend(marker.getLatLng());
    }
  });

  mapState.markers = nextMarkers;

  if (nextMarkers.length === 0) {
    const { center, zoom } = computeInitialView();
    map.setView(center, zoom);
    return;
  }

  const isSingleEventMap = window.singleEvent === true || window.singleEvent === 'true';

  if (nextMarkers.length === 1 && isSingleEventMap) {
    map.setView(nextMarkers[0].getLatLng(), 12);
  } else {
    map.fitBounds(bounds, { padding: [24, 24] });
  }
}

function getLocalEventsElements() {
  if (!localEventsState.container) {
    const container = document.getElementById('localEventsFloating');
    const panel = document.getElementById('localEventsList');

    localEventsState.container = container || null;
    localEventsState.panel = panel || null;
    localEventsState.listItems = document.getElementById('localEventsListItems');
    localEventsState.button = document.getElementById('localEventsButton');
    localEventsState.closeButton = container ? container.querySelector('.ds-map-floating__close') : null;
    localEventsState.countLabel = document.getElementById('localEventsCount');
    localEventsState.titleCountLabel = document.getElementById('localEventsTitleCount');

    if (localEventsState.panel && !localEventsState.panel.hasAttribute('tabindex')) {
      localEventsState.panel.setAttribute('tabindex', '-1');
    }
  }

  return localEventsState;
}

function toggleLocalEvents(desiredState) {
  const { container, panel, button, closeButton } = getLocalEventsElements();

  if (!container || !panel || !button) {
    console.error('Local events elements not found:', { container, panel, button });
    return;
  }

  const currentlyOpen = container.dataset.state === 'open';
  const requestedState = desiredState === 'open' || desiredState === 'closed'
    ? desiredState
    : (currentlyOpen ? 'closed' : 'open');

  if ((requestedState === 'open' && currentlyOpen) || (requestedState === 'closed' && !currentlyOpen)) {
    return;
  }

  const isOpening = requestedState === 'open';
  container.dataset.state = requestedState;
  button.setAttribute('aria-expanded', String(isOpening));
  panel.setAttribute('aria-hidden', String(!isOpening));

  if (isOpening) {
    populateLocalEvents();

    if (closeButton && typeof closeButton.focus === 'function') {
      try {
        closeButton.focus({ preventScroll: true });
      } catch (error) {
        closeButton.focus();
      }
      return;
    }

    if (typeof panel.focus === 'function') {
      try {
        panel.focus({ preventScroll: true });
      } catch (error) {
        panel.focus();
      }
    }
  } else if (typeof button.focus === 'function') {
    try {
      button.focus({ preventScroll: true });
    } catch (error) {
      button.focus();
    }
  }
}

function renderEmptyLocalEvents(listItems) {
  listItems.innerHTML = `
        <li class="ds-map-floating__item">
          <div class="ds-map-floating__item-content">
            <p class="ds-map-floating__item-summary">No activities that come to you available right now. Check back soon!</p>
          </div>
        </li>
      `;
}

function populateLocalEvents() {
  const { listItems } = getLocalEventsElements();
  if (!listItems) {
    console.error('localEventsListItems element not found');
    return;
  }

  const events = Array.isArray(window.eventsThatComeToYouData) ? window.eventsThatComeToYouData : [];

  if (events.length === 0) {
    renderEmptyLocalEvents(listItems);
    return;
  }

  listItems.textContent = '';

  const fragment = document.createDocumentFragment();

  events.forEach((event) => {
    const li = document.createElement('li');
    li.className = 'ds-map-floating__item';

    const eventUrl = escapeHtml(event.event_url || '#');
    const title = escapeHtml(event.title || 'Untitled Event');
    const summary = escapeHtml(event.platform_summary || 'This experience comes directly to your team.');
    const shouldRenderImage = event.image_url && event.is_yelp !== true && event.is_yelp !== 'true';
    const imageSrc = shouldRenderImage ? escapeHtml(event.image_url) : '';
    const ratingValue = event.average_rating ? parseFloat(event.average_rating).toFixed(2) : null;
    const reviewsCountRaw = event.all_reviews_count ?? event.reviews_count ?? 0;
    const reviewsCount = Number(reviewsCountRaw) || 0;

    const ratingMarkup = ratingValue && reviewsCount > 0
      ? `
            <span class="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-sm font-bold text-amber-700">
              <i data-lucide="star" class="lucide-icon rating-star text-amber-500" aria-hidden="true"></i>
              <span>${ratingValue}</span>
              <span class="text-sm font-normal text-neutral-500">(${reviewsCount.toLocaleString()})</span>
            </span>
          `
      : `<span class="text-sm font-normal uppercase tracking-wide text-neutral-400">New listing</span>`;

    li.innerHTML = `
        <div class="ds-map-floating__item-media">
          ${
            shouldRenderImage
              ? `<a href="${eventUrl}" class="ds-map-floating__item-image-link">
                   <img src="${imageSrc}" alt="${title}" loading="lazy" onerror="this.src='${window.eventImageMissingPath}'" />
                 </a>`
              : ''
          }
        </div>
        <div class="ds-map-floating__item-content">
          <a href="${eventUrl}" class="ds-map-floating__item-title-link">
            <h4 class="ds-map-floating__item-title">${title}</h4>
          </a>
          <p class="ds-map-floating__item-summary">${summary}</p>
          <div class="ds-map-floating__item-meta">
            ${ratingMarkup}
          </div>
        </div>
      `;

    fragment.appendChild(li);
  });

  listItems.appendChild(fragment);
}

function updateLocalEventsCount() {
  const {
    countLabel,
    titleCountLabel,
    button,
    panel,
    container
  } = getLocalEventsElements();

  const events = Array.isArray(window.eventsThatComeToYouData) ? window.eventsThatComeToYouData : [];
  const count = events.length;

  if (countLabel) countLabel.textContent = count;
  if (titleCountLabel) titleCountLabel.textContent = count;

  if (!button || !panel || !container) {
    console.error('Local events UI elements missing', { container, button, panel });
    return;
  }

  container.removeAttribute('hidden');

  if (container.dataset.state !== 'open') {
    container.dataset.state = 'open';
  }

  button.setAttribute('aria-expanded', 'true');
  panel.setAttribute('aria-hidden', 'false');

  if (count === 0) {
    const { listItems } = getLocalEventsElements();
    if (listItems) {
      renderEmptyLocalEvents(listItems);
    }
    return;
  }

  populateLocalEvents();
}

function setupLocalEventsInteractions() {
  if (localEventsState.initialized) {
    return;
  }

  const { container, panel, button, closeButton } = getLocalEventsElements();

  if (!container || !panel || !button) {
    return;
  }

  const toggleHandler = (event) => {
    event.preventDefault();
    toggleLocalEvents();
  };

  const closeHandler = (event) => {
    event.preventDefault();
    toggleLocalEvents('closed');
  };

  const keydownHandler = (event) => {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      toggleLocalEvents('closed');
    }
  };

  button.addEventListener('click', toggleHandler);

  if (closeButton) {
    closeButton.addEventListener('click', closeHandler);
  }

  panel.addEventListener('keydown', keydownHandler);

  localEventsState.handlers.toggle = toggleHandler;
  localEventsState.handlers.close = closeButton ? closeHandler : null;
  localEventsState.handlers.keydown = keydownHandler;
  localEventsState.initialized = true;
}

function resetLocalEventsState() {
  const { button, closeButton, panel, handlers } = localEventsState;

  if (button && handlers.toggle) {
    button.removeEventListener('click', handlers.toggle);
  }

  if (closeButton && handlers.close) {
    closeButton.removeEventListener('click', handlers.close);
  }

  if (panel && handlers.keydown) {
    panel.removeEventListener('keydown', handlers.keydown);
  }

  localEventsState.container = null;
  localEventsState.panel = null;
  localEventsState.listItems = null;
  localEventsState.button = null;
  localEventsState.closeButton = null;
  localEventsState.countLabel = null;
  localEventsState.titleCountLabel = null;
  localEventsState.initialized = false;
  localEventsState.handlers = {
    toggle: null,
    close: null,
    keydown: null
  };
}

async function fetchMapData() {
  const url = new URL(window.location.href);
  const lat = url.searchParams.get('latitude');
  const lng = url.searchParams.get('longitude');

  if (lat && lng) {
    url.searchParams.set('latitude', lat);
    url.searchParams.set('longitude', lng);
  }

  url.searchParams.set('format', 'js');

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    credentials: 'same-origin'
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch map data (${response.status})`);
  }

  return response.json();
}

async function hydrateMultiEventMap() {
  try {
    const pendingRequest = mapState.dataRequest || fetchMapData();
    mapState.dataRequest = pendingRequest;

    const data = await pendingRequest;
    window.initialEventsData = data.events_data || [];
    window.eventsThatComeToYouData = data.events_that_come_to_you_data || [];

    updateMap(window.initialEventsData);
    updateLocalEventsCount();
  } catch (error) {
    console.error('Error fetching map data:', error);
    window.initialEventsData = [];
    window.eventsThatComeToYouData = [];
    updateMap([]);
    updateLocalEventsCount();
  } finally {
    mapState.dataRequest = null;
  }
}

async function initializeMap() {
  await loadLeafletModule();

  if (typeof L === 'undefined') {
    console.error('Leaflet failed to load');
    return;
  }

  const map = ensureMap();
  if (!map) {
    return;
  }

  setupLocalEventsInteractions();

  if (mapInitialized) {
    if (Array.isArray(mapState.pendingEventsData)) {
      updateMap(mapState.pendingEventsData);
    }
    return;
  }

  mapInitialized = true;

  if (Array.isArray(mapState.pendingEventsData)) {
    updateMap(mapState.pendingEventsData);
  }

  if (window.singleEvent) {
    updateMap(window.singleEventData);
    return;
  }

  await hydrateMultiEventMap();
}

function setupMapLazyLoading() {
  const mapElement = getMapElement();
  if (!mapElement || mapState.placeholder) {
    return;
  }

  if (window.singleEvent) {
    const finalize = () => {
      mapElement.style.display = 'block';

      loadLeafletModule()
        .then(() => initializeMap())
        .catch((error) => {
          console.error('Failed to initialize single event map', error);
        });
    };

    finalize();
    return;
  }

  const initializeImmediately = () => {
    mapElement.style.display = 'block';

    const runInitialization = () => {
      loadLeafletModule()
        .then(() => initializeMap())
        .catch((error) => {
          console.error('Failed to initialize map without observer', error);
        });
    };

    runInitialization();
  };

  if (typeof IntersectionObserver === 'undefined') {
    initializeImmediately();
    return;
  }

  const placeholder = document.createElement('div');
  placeholder.className = 'map-placeholder';
  placeholder.style.cssText = `
    width: 100%;
    height: ${window.singleEvent ? '400px' : '500px'};
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 18px;
    font-weight: 500;
    position: relative;
    overflow: hidden;
  `;

  placeholder.innerHTML = `
    <div style="text-align: center; z-index: 2; position: relative;">
      <div style="margin-bottom: 10px;">
        <i data-lucide="map" class="lucide-icon" style="font-size: 48px; opacity: 0.7;"></i>
      </div>
      <div>Loading interactive map...</div>
      <div style="margin-top: 10px; font-size: 14px; opacity: 0.8;">
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>
    <div style="
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
      animation: shimmer 2s infinite;
    "></div>
    <style>
      @keyframes shimmer {
        0% { left: -100%; }
        100% { left: 100%; }
      }
    </style>
  `;

  mapElement.style.display = 'none';
  if (mapElement.parentNode) {
    mapElement.parentNode.insertBefore(placeholder, mapElement);
  } else {
    mapElement.style.display = 'block';
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !mapInitialized) {
        const finalizeInitialization = () => {
          loadLeafletModule()
            .then(() => {
              initializeMapAfterLoad(placeholder, mapElement);
            })
            .catch((error) => {
              console.error('Failed to load Leaflet for map initialization', error);
            });
        };

        finalizeInitialization();

        observer.unobserve(entry.target);
      }
    });
  }, {
    rootMargin: '300px 0px',
    threshold: 0.01
  });

  observer.observe(placeholder);

  mapState.observer = observer;
  mapState.placeholder = placeholder;
}

function initializeMapAfterLoad(placeholder, mapElement) {
  if (mapState.observer) {
    mapState.observer.disconnect();
    mapState.observer = null;
  }

  if (mapState.placeholder === placeholder) {
    mapState.placeholder = null;
  }

  placeholder.remove();
  mapElement.style.display = 'block';

  initializeMap().catch((error) => {
    console.error('Failed to initialize map after load', error);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  setupLocalEventsInteractions();
  setupMapLazyLoading();
});

document.addEventListener('turbo:load', () => {
  resetMapElementCache();
  mapInitialized = false;

  if (typeof cleanupMap === 'function') {
    cleanupMap();
  }

  setupLocalEventsInteractions();
  setupMapLazyLoading();
});

export { initializeMap };

window.initializeBookingMap = () => {
  setupMapLazyLoading();
};

document.dispatchEvent(new Event('booking:map:ready'));

function forceRefreshEvents() {
  cleanupMap();
  window.location.reload();
}

function checkEventsData() {
  console.log('Current events data status:', {
    initialEventsData: window.initialEventsData?.length || 0,
    eventsThatComeToYouData: window.eventsThatComeToYouData?.length || 0,
    singleEvent: window.singleEvent,
    timestamp: new Date().toISOString()
  });
}

window.updateMap = updateMap;
window.toggleLocalEvents = toggleLocalEvents;
window.forceRefreshEvents = forceRefreshEvents;
window.checkEventsData = checkEventsData;
window.cleanupMap = cleanupMap;
